import { useEffect, useState } from 'react';
import type { Offer, Product } from '@/lib/types';
import { formatMoney } from '@/lib/format';
import { applyOfferPercentage } from '@/lib/offers';
import ProductImage from '@/components/feature/ProductImage';
import { resolveImageUrl } from '@/lib/imageUrl';
import { buildImageSrcSet } from '@/lib/imageOptimizer';
import { getStockStatus } from '@/lib/types';

interface ProductDetailModalProps {
  product: Product | null;
  currencySymbol: string;
  whatsappLink: string | null;
  offer?: Offer | null;
  onClose: () => void;
}

export default function ProductDetailModal({
  product,
  currencySymbol,
  whatsappLink,
  offer,
  onClose,
}: ProductDetailModalProps) {
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (!product) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (previewOpen) setPreviewOpen(false);
        else onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [product, onClose, previewOpen]);

  if (!product) return null;

  const status = getStockStatus(product.stock, product.min_stock);
  const statusLabel =
    status === 'en_stock' ? 'Disponible' : status === 'bajo' ? 'Stock bajo' : 'Agotado';
  const previewSrc = resolveImageUrl(product.image_url);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />

      <div className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-hidden rounded-lg bg-background-50 border border-background-200 flex flex-col">
        {/* Imagen */}
        <div className="relative w-full h-64 bg-background-100 overflow-hidden shrink-0">
          <button
            type="button"
            onClick={() => previewSrc && setPreviewOpen(true)}
            disabled={!previewSrc}
            aria-label="Ver imagen en grande"
            className={`absolute inset-0 w-full h-full ${previewSrc ? 'cursor-zoom-in' : 'cursor-default'}`}
          >
            <ProductImage
              url={product.image_url}
              name={product.name}
              className="w-full h-full object-cover object-top"
              placeholderClassName="w-full h-full"
              iconClassName="text-5xl"
              sizes="(min-width: 768px) 512px, 100vw"
              priority
            />
          </button>
          {previewSrc && (
            <span className="pointer-events-none absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-black/50 px-2.5 py-1 text-xs font-medium text-background-50">
              <i className="ri-zoom-in-line text-sm" /> Ver imagen
            </span>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-black/50 text-background-50 hover:bg-black/70 transition cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Contenido con scroll */}
        <div className="flex-1 overflow-y-auto p-5 md:p-6">
          <div className="flex items-start justify-between gap-3">
            <h2 className="font-heading text-xl font-bold text-foreground-950 leading-snug">
              {product.name}
            </h2>
            <span
              className={`shrink-0 inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1 ${
                status === 'en_stock'
                  ? 'bg-accent-100 text-accent-900'
                  : status === 'bajo'
                    ? 'bg-secondary-100 text-secondary-900'
                    : 'bg-primary-100 text-primary-800'
              }`}
            >
              <i
                className={
                  status === 'en_stock'
                    ? 'ri-checkbox-circle-fill'
                    : status === 'bajo'
                      ? 'ri-alert-fill'
                      : 'ri-close-circle-fill'
                }
              />
              {statusLabel}
            </span>
          </div>

          <p className="mt-3 text-2xl font-heading font-bold text-primary-700">
            {formatMoney(
              offer
                ? applyOfferPercentage(Number(product.sale_price), Number(offer.percentage))
                : Number(product.sale_price),
              currencySymbol
            )}
          </p>

          {offer && (
            <div className="mt-2 flex items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 text-background-50 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide">
                <i className="ri-fire-line" /> OFERTA
              </span>
              <span className="text-sm text-foreground-400 line-through">
                {formatMoney(Number(product.sale_price), currencySymbol)}
              </span>
              <span className="text-sm font-semibold text-accent-700">
                -{Number(offer.percentage)}%
              </span>
            </div>
          )}

          {/* Detalles */}
          <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 text-sm">
            {product.brand && (
              <div className="flex flex-col">
                <dt className="text-xs text-foreground-400">Marca</dt>
                <dd className="mt-0.5 text-foreground-800 font-medium">{product.brand}</dd>
              </div>
            )}
            {product.category?.name && (
              <div className="flex flex-col">
                <dt className="text-xs text-foreground-400">Categoría</dt>
                <dd className="mt-0.5 text-foreground-800 font-medium">{product.category.name}</dd>
              </div>
            )}
            {product.sku && (
              <div className="flex flex-col">
                <dt className="text-xs text-foreground-400">SKU</dt>
                <dd className="mt-0.5 text-foreground-800 font-medium">{product.sku}</dd>
              </div>
            )}
            {product.barcode && (
              <div className="flex flex-col">
                <dt className="text-xs text-foreground-400">Código de barra</dt>
                <dd className="mt-0.5 text-foreground-800 font-medium">{product.barcode}</dd>
              </div>
            )}
          </dl>

          {product.description && (
            <div className="mt-5">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-foreground-400">
                Descripción
              </h3>
              <p className="mt-2 text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                {product.description}
              </p>
            </div>
          )}
        </div>

        {/* Pie con WhatsApp */}
        <div className="p-4 md:p-5 border-t border-background-200 shrink-0">
          {whatsappLink ? (
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-accent-500 hover:bg-accent-600 px-4 py-3 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
            >
              <i className="ri-whatsapp-line text-lg" /> Consultar por WhatsApp
            </a>
          ) : (
            <span className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-background-100 px-4 py-3 text-sm font-medium text-foreground-500 whitespace-nowrap">
              Sin WhatsApp configurado
            </span>
          )}
        </div>
      </div>

      {/* Vista previa de la imagen a pantalla completa */}
      {previewOpen && previewSrc && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4">
          <button
            type="button"
            onClick={() => setPreviewOpen(false)}
            aria-label="Cerrar vista previa"
            className="absolute top-4 right-4 w-11 h-11 flex items-center justify-center rounded-full bg-black/50 text-background-50 hover:bg-black/70 transition cursor-pointer"
          >
            <i className="ri-close-line text-2xl" />
          </button>
          <img
            src={previewSrc}
            srcSet={buildImageSrcSet(product.image_url)}
            sizes="100vw"
            alt={product.name}
            title={product.name}
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            className="max-w-full max-h-full object-contain rounded-lg select-none"
            onClick={() => setPreviewOpen(false)}
          />
        </div>
      )}
    </div>
  );
}